<template>
  <div>
    <v-dialog v-model="open" max-width="600px">
      <v-card>
        <v-card-title>クラス設定</v-card-title>

        <v-form v-model="valid">
          <v-card-text>
            <v-container>
              <v-row>
                <v-col cols="6" align-self="center">
                  <div>クラス名</div>
                </v-col>
                <v-col cols="6" align-self="center">
                  <v-text-field v-model="className" :rules="[required]" variant="outlined" density="compact" hide-details color="primary"></v-text-field>
                </v-col>
              </v-row>

              <v-row>
                <v-col cols="6" align-self="center">
                  <div>対象年度</div>
                </v-col>
                <v-col cols="6" align-self="center">
                  <v-text-field v-model="year" :rules="[required]" variant="outlined" density="compact" hide-details type="number" color="primary">
                    <template #append-inner>
                      <span class="text-medium-emphasis text-no-wrap align-self-center">年度</span>
                    </template>
                  </v-text-field>
                </v-col>
              </v-row>

              <v-row>
                <v-col cols="6" align-self="center">
                  <div>クラス園児の年齢</div>
                </v-col>
                <v-col cols="6" align-self="center">
                  <v-text-field v-model="childrenAge" :rules="[required]" variant="outlined" density="compact" hide-details type="number" color="primary">
                    <template #append-inner>
                      <span class="text-medium-emphasis text-no-wrap align-self-center">歳</span>
                    </template>
                  </v-text-field>
                </v-col>
              </v-row>
            </v-container>
          </v-card-text>

          <v-card-actions>
            <v-container>
              <v-row>
                <v-btn @click="close" variant="text" color="grey">キャンセル</v-btn>
                <v-btn v-if="dialogType == 'update'" @click="deleteClass" class="mx-2" color="error" variant="flat">削除</v-btn>
                <v-spacer />
                <div v-if="dialogType == 'create'">
                  <v-btn @click="addClass" :loading="loading" :disabled="!valid" color="primary" variant="flat">作成</v-btn>
                </div>
                <div v-else>
                  <v-btn @click="updateClass" :loading="loading" :disabled="!valid" color="primary" variant="flat">更新</v-btn>
                </div>
              </v-row>
            </v-container>
          </v-card-actions>
        </v-form>
      </v-card>
    </v-dialog>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';
//import DialogMixin from '@/mixins/DialogMixin';

import { classEditModule } from '@/store/dataModules/classEditModule';
import ValidationMixin from '@/mixins/ValidationMixin';
import LocalDataService from '@/service/LocalDataService';
import Swal from 'sweetalert2';

export type dialogType = "create" | "update";

export default defineComponent({
  name: 'ClassDialogContainer',

  // DialogMixin は削除し、このコンポーネント内で完結させます
  //mixins: [DialogMixin,ValidationMixin],
  mixins: [ValidationMixin],

  props: {
    dialogType: {
      type: String as PropType<dialogType>,
      default: "create",
    },
    // Vue 3 の双方向バインディングで親から受け取るための定義
    isOpen: {
      type: Boolean,
      required: true,
      default: false,
    }
  },

  emits: ['update:isOpen'],

  data() {
    return {
      valid: false,
      loading: false,
    }
  },

  computed: {
    // ダイアログの開閉を管理するスマートな算出プロパティ
    open: {
      get() {
        return this.isOpen; // 親の値をそのまま使う
      },
      set(value: boolean) {
        // ダイアログが閉じようとした瞬間、自動で親に「falseにして」とお願いする
        this.$emit('update:isOpen', value);
      }
    },
    className: {
      get() { return classEditModule.className; },
      set(value: string) { classEditModule.setClassName(value); }
    },
    year: {
      get() { return classEditModule.year; },
      set(value: number) { classEditModule.setYear(value); }
    },
    childrenAge: {
      get() { return classEditModule.childrenAge; },
      set(value: string) { classEditModule.setChildrenAge(value); }
    },
  },

  methods: {
    // キャンセルボタンを押した時
    close() {
      this.open = false; // これだけで自動的に親のデータも更新されます！
    },

    async addClass() {
      this.loading = true;
      const id = LocalDataService.getNurseryId();
      if (id) {
        const response = await classEditModule.addClass({ nurseryId: id });
        if (response) {
          Swal.fire({ title: 'クラスを作成しました', icon: 'success' });
          this.open = false;
        }
      }
      this.loading = false;
    },

    async updateClass() {
      this.loading = true;
      const id = LocalDataService.getNurseryId();
      if (id) {
        const response = await classEditModule.updateClass({ nurseryId: id });
        if (response) {
          Swal.fire({ title: 'クラスを更新しました', icon: 'success' });
          this.open = false; 
        }
      }
      this.loading = false;
    },

    async deleteClass() {
      const nurseryId = LocalDataService.getNurseryId();
      if (nurseryId) {
        Swal.fire({
          title: `${this.className} を削除しますか？`,
          text: "この操作は取り消せません",
          icon: 'warning',
          showCancelButton: true,
          confirmButtonColor: 'red',
          cancelButtonColor: 'grey',
          confirmButtonText: '削除します',
          cancelButtonText: 'キャンセル',
        }).then((result) => {
          if (result.isConfirmed) {
            this.loading = true;
            classEditModule.deleteClass({ nurseryId: nurseryId })
              .then(() => {
                this.loading = false;
                Swal.fire({ title: '削除しました', icon: 'success' });
                this.open = false;
              }).catch(() => {
                this.loading = false;
                Swal.fire({ title: '削除に失敗しました', icon: 'error' });
              });
          }
        });
      }
    },
  }
})
</script>